import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FileUp, ImagePlus, RotateCcw, Download, SlidersHorizontal, Trash2, Type, Move, X, Settings, Maximize } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import appPackage from '../package.json';

const I18N = {
    es: {
        appTitle: 'AquaSigna',
        appSubtitle: 'Protegé tus fotos con estilo',
        by: 'Por:',
        author: 'Javier Valverde Salvatierra',
        company: 'Soluciones Inmediatas de San José, S.A.',
        basePlaceholder1: 'Sube una imagen base',
        basePlaceholder2: 'y luego añade marcas de agua o texto',
        addElementHint: 'Añade una Marca de Agua o un Texto',
        loading: 'Cargando...',
        selectBaseImageTitle: 'Seleccionar Imagen Base: Sube la imagen principal sobre la que trabajarás.',
        addWatermarkTitle: 'Añadir Marca de Agua: Sube una imagen para usarla como marca de agua. Requiere una imagen base.',
        addTextTitle: 'Añadir Texto: Agrega un nuevo texto a la imagen para personalizarla. Requiere una imagen base.',
        adjustTitle: 'Ajustar Elemento: Abre un panel para cambiar la escala, opacidad, o color del elemento seleccionado.',
        resetTitle: 'Reiniciar: Borra todas las imágenes y elementos, y restablece la aplicación.',
        downloadTitle: 'Descargar Imagen: Guarda la imagen base con todas las marcas de agua y texto aplicados.',
        settingsTitleButton: 'Ajustes: Idioma, ayuda y datos de la empresa.',
        settingsModalTitle: 'Ajustes y Ayuda',
        close: 'Cerrar',
        languageSection: 'Idioma',
        languageSpanish: 'Español',
        languageEnglish: 'Inglés',
        companySection: 'Acerca de',
        versionLabel: 'Versión:',
        contactEmail: 'Correo:',
        contactWeb: 'Web:',
        helpSection: 'Ayuda Rápida',
        helpBaseImage: 'Seleccionar Imagen Base: Sube la imagen principal sobre la que trabajarás.',
        helpWatermark: 'Añadir Marca de Agua: Puedes añadir varias marcas y manipularlas individualmente.',
        helpText: 'Añadir Texto: Agrega texto y luego ajusta tamaño, color, opacidad y posición.',
        helpAdjust: 'Ajustar Elemento: Selecciona un elemento en el lienzo y modifica sus propiedades.',
        helpReset: 'Reiniciar: Limpia la edición y vuelve al estado inicial.',
        helpDownload: 'Descargar Imagen: Guarda la imagen final en tu dispositivo.',
        helpFitScreen: 'Ajustar a Pantalla: Centra y escala la imagen para verla completa. Usa la rueda del ratón para hacer zoom.',
        removeElement: 'Eliminar Elemento',
        dragHint: 'Arrastra el elemento en la imagen para moverlo.',
        addTextModalTitle: 'Añadir Texto',
        addTextPlaceholder: 'Introduce tu texto aquí...',
        createText: 'Crear Texto',
        cancel: 'Cancelar',
        successDownloadAndroid: 'Imagen descargada en la carpeta Download.',
        successDownloadWeb: 'Imagen descargada correctamente.',
        errNeedBaseDownload: 'Sube una imagen base antes de descargar.',
        errDownloadDevice: 'No se pudo descargar la imagen en este dispositivo:',
        errUnknown: 'Error desconocido',
        errBaseDownload: 'Error al cargar la imagen base original para la descarga.',
        errLoadBase: 'Error al cargar la imagen base.',
        errNeedBaseWatermark: 'Por favor, sube una imagen base antes de añadir una marca de agua.',
        errLoadWatermark: 'Error al cargar la imagen de marca de agua.',
        errNeedBaseText: 'Por favor, sube una imagen base antes de añadir texto.',
        fitScreenTitle: 'Ajustar a pantalla: Centra y ajusta la imagen para verla completa.',
    },
    en: {
        appTitle: 'AquaSigna',
        appSubtitle: 'Your signature on every image',
        by: 'By:',
        author: 'Javier Valverde Salvatierra',
        company: 'Soluciones Inmediatas de San José, S.A.',
        basePlaceholder1: 'Upload a base image',
        basePlaceholder2: 'then add watermarks or text',
        addElementHint: 'Add a Watermark or Text',
        loading: 'Loading...',
        selectBaseImageTitle: 'Select Base Image: Upload the main image you will work on.',
        addWatermarkTitle: 'Add Watermark: Upload an image to use as a watermark. Requires a base image.',
        addTextTitle: 'Add Text: Add new text to personalize the image. Requires a base image.',
        adjustTitle: 'Adjust Element: Open a panel to change scale, opacity, or color of the selected element.',
        resetTitle: 'Reset: Clears all images and elements and restores the app.',
        downloadTitle: 'Download Image: Save the base image with all applied watermarks and text.',
        settingsTitleButton: 'Settings: Language, help, and company data.',
        settingsModalTitle: 'Settings and Help',
        close: 'Close',
        languageSection: 'Language',
        languageSpanish: 'Spanish',
        languageEnglish: 'English',
        companySection: 'About',
        versionLabel: 'Version:',
        contactEmail: 'Email:',
        contactWeb: 'Website:',
        helpSection: 'Quick Help',
        helpBaseImage: 'Select Base Image: Upload the main image you will work on.',
        helpWatermark: 'Add Watermark: You can add multiple watermarks and edit each one individually.',
        helpText: 'Add Text: Add text, then adjust size, color, opacity, and position.',
        helpAdjust: 'Adjust Element: Select an element on canvas and modify its properties.',
        helpReset: 'Reset: Clears the current editing state.',
        helpDownload: 'Download Image: Saves the final image on your device.',
        helpFitScreen: 'Fit to Screen: Centers and scales the image to see it all. Use the mouse wheel to zoom.',
        removeElement: 'Delete Element',
        dragHint: 'Drag the element over the image to move it.',
        addTextModalTitle: 'Add Text',
        addTextPlaceholder: 'Type your text here...',
        createText: 'Create Text',
        cancel: 'Cancel',
        successDownloadAndroid: 'Image downloaded to the Download folder.',
        successDownloadWeb: 'Image downloaded successfully.',
        errNeedBaseDownload: 'Upload a base image before downloading.',
        errDownloadDevice: 'Could not download the image on this device:',
        errUnknown: 'Unknown error',
        errBaseDownload: 'Error loading the original base image for download.',
        errLoadBase: 'Error loading base image.',
        errNeedBaseWatermark: 'Please upload a base image before adding a watermark.',
        errLoadWatermark: 'Error loading watermark image.',
        errNeedBaseText: 'Please upload a base image before adding text.',
        fitScreenTitle: 'Fit to screen: Center and scale the image to see it all.',
    },
};

const App = () => {
    const APP_VERSION = appPackage.version;

    // Estados para las imágenes y sus propiedades
    const [baseImageSrc, setBaseImageSrc] = useState(null); // URL de la imagen base
    const [baseImageRotation, setBaseImageRotation] = useState(0); // Nuevo estado para la rotación de la imagen base
    const [watermarks, setWatermarks] = useState([]); // [{ id, src, obj, x, y, scale, opacity, rotation, name }]
    const [nextWatermarkId, setNextWatermarkId] = useState(0); // Para generar IDs únicos para las marcas de agua
    const [activeWatermarkId, setActiveWatermarkId] = useState(null); // ID de la marca de agua actualmente seleccionada

    // Estados para el texto
    const [texts, setTexts] = useState([]); // [{ id, content, x, y, fontSize, color, fontFamily, opacity, rotation }]
    const [nextTextId, setNextTextId] = useState(0);
    const [activeTextId, setActiveTextId] = useState(null);
    const [newTextContent, setNewTextContent] = useState(''); // Para el input de texto del modal

    const [loading, setLoading] = useState(false); // Estado de carga
    const [error, setError] = useState(null); // Estado de error
    const [successMessage, setSuccessMessage] = useState(null); // Mensaje de confirmación
    const [showAdjustmentPanel, setShowAdjustmentPanel] = useState(false); // Controla la visibilidad del panel de ajustes modal
    const [showHelpModal, setShowHelpModal] = useState(false); // Nuevo estado para controlar la visibilidad del modal de ayuda
    const [showAddTextModal, setShowAddTextModal] = useState(false); // Nuevo estado para el modal de añadir texto
    const [modalOpacity, setModalOpacity] = useState(1.0); // Nuevo estado para la opacidad del modal
    const [language, setLanguage] = useState(() => {
        const savedLanguage = window.localStorage.getItem('app-language');
        return savedLanguage === 'en' ? 'en' : 'es';
    });

    // Estados para la funcionalidad de arrastre
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartY, setDragStartY] = useState(0);
    const [initialElementX, setInitialElementX] = useState(0);
    const [initialElementY, setInitialElementY] = useState(0);

    // Estados para el pan y zoom del canvas
    const viewportRef = useRef({ x: 0, y: 0, zoom: 1 });
    const [viewportTick, setViewportTick] = useState(0); // eslint-disable-line no-unused-vars
    const isPanningRef = useRef(false);
    const panStartRef = useRef({ x: 0, y: 0, vpX: 0, vpY: 0 });
    const dragModeRef = useRef(null); // 'element' | 'pan' | null
    const [showZoomOverlay, setShowZoomOverlay] = useState(false);
    const zoomOverlayTimerRef = useRef(null);
    const pinchRef = useRef({ active: false, startDist: 0, startZoom: 1 });

    // Estados para el arrastre del panel de ajustes
    const [isPanelDragging, setIsPanelDragging] = useState(false);
    const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 });
    const [initialPanelPosition, setInitialPanelPosition] = useState({ x: 0, y: 0 });

    // Referencias para el arrastre y la animación
    const currentDragOffsetRef = useRef({ dx: 0, dy: 0 }); // Almacena el desplazamiento actual durante el arrastre
    const animationFrameIdRef = useRef(null); // ID del requestAnimationFrame para cancelar
    const activeElementRef = useRef(null); // Referencia al elemento activo (marca de agua o texto)
    const loadedBaseImageRef = useRef(null); // Referencia para almacenar el objeto Image de la imagen base ya cargada
    const successTimeoutRef = useRef(null); // Temporizador para ocultar el mensaje de éxito

    // Referencias al elemento canvas y a los inputs de archivo
    const canvasRef = useRef(null);
    const baseImageInputRef = useRef(null); // Referencia para el input de la imagen base
    const watermarkInputRef = useRef(null); // Referencia para el input de la marca de agua

    // Referencia para el contenedor del canvas, que observaremos con ResizeObserver
    const canvasContainerRef = useRef(null);

    // Referencia para el panel de ajustes
    const adjustmentPanelRef = useRef(null);

    // Usamos un Map para almacenar los límites de cada elemento (marca de agua o texto) por su ID
    const allElementBounds = useRef(new Map());

    // Lista de fuentes disponibles
    const FONT_OPTIONS = ['Inter', 'Arial', 'Verdana', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New', 'Lucida Console', 'Cursive', 'Harrington'];
    const t = useCallback((key) => I18N[language][key] || I18N.es[key] || key, [language]);


    // Función auxiliar para cargar una imagen
    const loadImage = useCallback((src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
    }, []);

    // Función para dibujar las imágenes y el texto en el canvas
    const drawImagesOnCanvas = useCallback(async (drawSelectionBorder = true, dragOffset = { dx: 0, dy: 0 }) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height); // Limpiar el canvas

        try {
            const baseImage = loadedBaseImageRef.current;
            allElementBounds.current.clear(); // Limpiar los límites antes de redibujar

            const vp = viewportRef.current;

            // Dibujar fondo con patrón de puntos (en espacio de pantalla, antes del transform)
            const { width, height } = canvas;
            ctx.fillStyle = '#f8f9fc';
            ctx.fillRect(0, 0, width, height);

            const dotSpacing = 20;
            const dotRadius = 1;
            ctx.fillStyle = 'rgba(99, 102, 241, 0.12)';
            for (let x = dotSpacing; x < width; x += dotSpacing) {
                for (let y = dotSpacing; y < height; y += dotSpacing) {
                    ctx.beginPath();
                    ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            // Aplicar transformación del viewport
            ctx.save();
            ctx.translate(vp.x, vp.y);
            ctx.scale(vp.zoom, vp.zoom);

            if (baseImage && baseImageSrc) {
                const imgW = baseImage.width;
                const imgH = baseImage.height;

                ctx.save();
                ctx.translate(imgW / 2, imgH / 2);
                ctx.rotate(baseImageRotation * Math.PI / 180);
                ctx.drawImage(baseImage, -imgW / 2, -imgH / 2, imgW, imgH);
                ctx.restore();

            } else {
                // Tamaño virtual del area de trabajo cuando no hay imagen
                const virtualW = canvas.width / vp.zoom;
                const virtualH = canvas.height / vp.zoom;
                const centerX = virtualW / 2;
                const centerY = virtualH / 2;

                ctx.fillStyle = '#94a3b8';
                ctx.font = '14px Inter';
                ctx.textAlign = 'center';

                if (!baseImageSrc && watermarks.length === 0 && texts.length === 0) {
                     ctx.fillText(t('basePlaceholder1'), centerX, centerY - 10);
                     ctx.fillText(t('basePlaceholder2'), centerX, centerY + 10);
                } else if (baseImageSrc && watermarks.length === 0 && texts.length === 0) {
                    ctx.fillText(t('addElementHint'), centerX, centerY + 10);
                }
            }

            // Dibujar todas las marcas de agua
            for (const watermark of watermarks) {
                if (baseImage && watermark.obj) {
                    let currentX = watermark.x;
                    let currentY = watermark.y;

                    if (isDragging && activeWatermarkId === watermark.id && activeElementRef.current) {
                        currentX = initialElementX + dragOffset.dx;
                        currentY = initialElementY + dragOffset.dy;
                    }

                    const scaledWatermarkWidth = watermark.obj.width * watermark.scale;
                    const scaledWatermarkHeight = watermark.obj.height * watermark.scale;

                    const centerX = currentX + scaledWatermarkWidth / 2;
                    const centerY = currentY + scaledWatermarkHeight / 2;

                    ctx.save();
                    ctx.translate(centerX, centerY);
                    ctx.rotate(watermark.rotation * Math.PI / 180);
                    
                    allElementBounds.current.set(`watermark-${watermark.id}`, {
                        type: 'watermark',
                        x: currentX,
                        y: currentY,
                        width: scaledWatermarkWidth,
                        height: scaledWatermarkHeight,
                    });

                    ctx.globalAlpha = watermark.opacity;
                    ctx.drawImage(watermark.obj, -scaledWatermarkWidth / 2, -scaledWatermarkHeight / 2, scaledWatermarkWidth, scaledWatermarkHeight);

                    if (drawSelectionBorder && activeWatermarkId === watermark.id) {
                        ctx.strokeStyle = '#6366f1';
                        ctx.lineWidth = 3;
                        ctx.strokeRect(-scaledWatermarkWidth / 2, -scaledWatermarkHeight / 2, scaledWatermarkWidth, scaledWatermarkHeight);
                    }
                    ctx.restore();
                }
            }

            // Dibujar todos los elementos de texto
            for (const text of texts) {
                if (baseImage) {
                    let currentX = text.x;
                    let currentY = text.y;

                    if (isDragging && activeTextId === text.id && activeElementRef.current) {
                        currentX = initialElementX + dragOffset.dx;
                        currentY = initialElementY + dragOffset.dy;
                    }

                    ctx.font = `${text.fontSize}px '${text.fontFamily}'`; // Usa la fuente seleccionada
                    ctx.fillStyle = text.color;
                    ctx.globalAlpha = text.opacity;
                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'top';

                    const textMetrics = ctx.measureText(text.content);
                    const textWidth = textMetrics.width;
                    const textHeight = text.fontSize * 1.2; // Altura aproximada
                    
                    const centerX = currentX + textWidth / 2;
                    const centerY = currentY + textHeight / 2;

                    ctx.save();
                    ctx.translate(centerX, centerY);
                    ctx.rotate(text.rotation * Math.PI / 180);

                    allElementBounds.current.set(`text-${text.id}`, {
                        type: 'text',
                        x: currentX,
                        y: currentY,
                        width: textWidth,
                        height: textHeight,
                    });

                    ctx.fillText(text.content, -textWidth / 2, -textHeight / 2);

                    if (drawSelectionBorder && activeTextId === text.id) {
                        ctx.strokeStyle = '#f59e0b';
                        ctx.lineWidth = 2; // Un poco más delgado para texto
                        ctx.strokeRect(-textWidth / 2, -textHeight / 2, textWidth, textHeight);
                    }
                    ctx.restore();
                }
            }

            // Restaurar el estado del canvas (deshacer viewport transform)
            ctx.restore();

        } catch (err) {
            console.error("Error al dibujar en el canvas:", err);
        }
    }, [baseImageSrc, watermarks, texts, activeWatermarkId, activeTextId, isDragging, initialElementX, initialElementY, baseImageRotation, t]);

    const animateDrag = useCallback(() => {
        drawImagesOnCanvas(true, currentDragOffsetRef.current);
        animationFrameIdRef.current = requestAnimationFrame(animateDrag);
    }, [drawImagesOnCanvas]);

    useEffect(() => {
        if (isDragging) {
            if (!animationFrameIdRef.current) {
                animationFrameIdRef.current = requestAnimationFrame(animateDrag);
            }
        } else {
            if (animationFrameIdRef.current) {
                cancelAnimationFrame(animationFrameIdRef.current);
                animationFrameIdRef.current = null;
            }
            drawImagesOnCanvas();
        }
        return () => {
            if (animationFrameIdRef.current) {
                cancelAnimationFrame(animationFrameIdRef.current);
                animationFrameIdRef.current = null;
            }
        };
    }, [isDragging, animateDrag, drawImagesOnCanvas]);


    useEffect(() => {
        const canvas = canvasRef.current;
        const container = canvasContainerRef.current;
        if (!canvas || !container) return;

        let rafId = null;

        const resizeCanvas = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const rect = canvas.getBoundingClientRect();
                const w = Math.round(rect.width);
                const h = Math.round(rect.height);
                if (w > 0 && h > 0 && (canvas.width !== w || canvas.height !== h)) {
                    canvas.width = w;
                    canvas.height = h;
                    // Auto-ajustar imagen al nuevo tamaño si hay imagen cargada
                    const img = loadedBaseImageRef.current;
                    if (img) {
                        const padding = 20;
                        const zoom = Math.min((w - padding * 2) / img.width, (h - padding * 2) / img.height);
                        viewportRef.current = {
                            x: (w - img.width * zoom) / 2,
                            y: (h - img.height * zoom) / 2,
                            zoom,
                        };
                    }
                    setViewportTick(v => v + 1);
                    drawImagesOnCanvas();
                }
            });
        };

        const resizeObserver = new ResizeObserver(resizeCanvas);
        resizeObserver.observe(container);
        resizeObserver.observe(canvas);

        // Respaldo: listener de resize en ventana
        window.addEventListener('resize', resizeCanvas);

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            resizeObserver.unobserve(container);
            resizeObserver.unobserve(canvas);
            window.removeEventListener('resize', resizeCanvas);
        };
    }, [drawImagesOnCanvas]);


    const handleBaseImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setLoading(true);
            setError(null);
            const reader = new FileReader();
            reader.onloadend = async () => {
                try {
                    const img = await loadImage(reader.result);
                    setBaseImageSrc(reader.result);
                    setBaseImageRotation(0); // Resetear rotación al cargar nueva imagen
                    loadedBaseImageRef.current = img;
                    setWatermarks([]);
                    setTexts([]);
                    setActiveWatermarkId(null);
                    setActiveTextId(null);
                    setNextWatermarkId(0);
                    setNextTextId(0);
                    setLoading(false);

                    // Auto-fit: ajustar la imagen al canvas
                    requestAnimationFrame(() => {
                        const canvas = canvasRef.current;
                        if (!canvas || !img) return;
                        const padding = 20;
                        const availW = canvas.width - padding * 2;
                        const availH = canvas.height - padding * 2;
                        const zoom = Math.min(availW / img.width, availH / img.height);
                        viewportRef.current = {
                            x: (canvas.width - img.width * zoom) / 2,
                            y: (canvas.height - img.height * zoom) / 2,
                            zoom,
                        };
                        setViewportTick(v => v + 1);
                        drawImagesOnCanvas();
                    });
                } catch (err) {
                    setError(t('errLoadBase'));
                    setLoading(false);
                }
            };
            reader.onerror = () => {
                setError(t('errLoadBase'));
                setLoading(false);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddWatermark = (event) => {
        if (!baseImageSrc) {
            setError(t('errNeedBaseWatermark'));
            event.target.value = '';
            return;
        }

        const file = event.target.files[0];
        if (file) {
            setLoading(true);
            setError(null);
            const reader = new FileReader();
            reader.onloadend = async () => {
                try {
                    const img = await loadImage(reader.result);
                    const baseImg = loadedBaseImageRef.current;
                    const baseRef = baseImg ? Math.max(baseImg.width, baseImg.height) : 500;
                    // Escala proporcional: la marca de agua al 30% del tamaño de la imagen base
                    const scale = Math.min(0.5, (baseRef * 0.3) / Math.max(img.width, img.height));
                    // Centrar en el área visible
                    const canvas = canvasRef.current;
                    const vp = viewportRef.current;
                    const worldCX = canvas ? (canvas.width / 2 - vp.x) / vp.zoom : baseRef / 2;
                    const worldCY = canvas ? (canvas.height / 2 - vp.y) / vp.zoom : baseRef / 2;
                    const newWatermark = {
                        id: nextWatermarkId,
                        src: reader.result,
                        obj: img,
                        x: worldCX - (img.width * scale) / 2,
                        y: worldCY - (img.height * scale) / 2,
                        scale,
                        opacity: 1.0,
                        rotation: 0,
                        name: file.name,
                    };
                    setWatermarks(prev => [...prev, newWatermark]);
                    // setActiveWatermarkId(newWatermark.id); // No seleccionar automáticamente
                    setActiveTextId(null);
                    setNextWatermarkId(prev => prev + 1);
                    setLoading(false);
                    setError(null);
                } catch (err) {
                    setError(t('errLoadWatermark'));
                    setLoading(false);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddText = () => {
        if (!baseImageSrc) {
            setError(t('errNeedBaseText'));
            return;
        }
        setShowAddTextModal(true);
    };

    const handleCreateText = () => {
        if (newTextContent.trim() !== '') {
            const canvas = canvasRef.current;
            const vp = viewportRef.current;
            const img = loadedBaseImageRef.current;
            // Tamaño proporcional a la imagen base
            const refSize = img ? Math.max(img.width, img.height) : 500;
            const fontSize = Math.round(refSize * 0.04);
            // Posicionar en el centro del área visible
            const worldCenterX = canvas ? (canvas.width / 2 - vp.x) / vp.zoom : refSize / 2;
            const worldCenterY = canvas ? (canvas.height / 2 - vp.y) / vp.zoom : refSize / 2;
            const newText = {
                id: nextTextId,
                content: newTextContent,
                x: worldCenterX,
                y: worldCenterY,
                fontSize,
                color: '#ffffff',
                rotation: 0,
                opacity: 1.0,
                fontFamily: 'Inter',
            };
            setTexts(prev => [...prev, newText]);
            setActiveTextId(newText.id);
            setActiveWatermarkId(null);
            setNextTextId(prev => prev + 1);
            setNewTextContent('');
            setShowAddTextModal(false);
        }
    };

    // --- Funciones para arrastrar la marca de agua ---
    const getEventCoords = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        let clientX, clientY;
        if (event.touches && event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }

        const xCss = clientX - rect.left;
        const yCss = clientY - rect.top;

        const scaleX = canvas.width / canvas.offsetWidth;
        const scaleY = canvas.height / canvas.offsetHeight;

        // Coordenadas en pantalla del canvas (no del mundo)
        const screenX = xCss * scaleX;
        const screenY = yCss * scaleY;

        // Convertir a coordenadas del mundo (descontar pan y zoom)
        const vp = viewportRef.current;
        const worldX = (screenX - vp.x) / vp.zoom;
        const worldY = (screenY - vp.y) / vp.zoom;

        return { x: worldX, y: worldY, screenX, screenY };
    };

    const handleInteractionStart = (event) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        if (event.touches && event.touches.length > 0) {
            event.preventDefault();
        }

        // Detectar pinch-to-zoom (2 dedos)
        if (event.touches && event.touches.length === 2) {
            const dx = event.touches[0].clientX - event.touches[1].clientX;
            const dy = event.touches[0].clientY - event.touches[1].clientY;
            pinchRef.current = {
                active: true,
                startDist: Math.sqrt(dx * dx + dy * dy),
                startZoom: viewportRef.current.zoom,
            };
            dragModeRef.current = null;
            isPanningRef.current = false;
            return;
        }

        // Si estaba en pinch y vuelve a 1 dedo, cancelar
        if (pinchRef.current.active) {
            pinchRef.current.active = false;
            return;
        }

        const { x, y, screenX, screenY } = getEventCoords(event);

        // Solo intentar seleccionar elemento si hay imagen base
        if (baseImageSrc) {
            let clickedElementId = null;
            let clickedElementType = null;
            const elementsArray = Array.from(allElementBounds.current.entries());

            for (let i = elementsArray.length - 1; i >= 0; i--) {
                const [id, bounds] = elementsArray[i];
                if (x >= bounds.x && x <= bounds.x + bounds.width && y >= bounds.y && y <= bounds.y + bounds.height) {
                    clickedElementId = id;
                    clickedElementType = bounds.type;
                    break;
                }
            }

            if (clickedElementId !== null) {
                if (clickedElementType === 'watermark') {
                    setActiveWatermarkId(Number(clickedElementId.split('-')[1]));
                    setActiveTextId(null);
                    activeElementRef.current = watermarks.find(wm => wm.id === Number(clickedElementId.split('-')[1]));
                } else if (clickedElementType === 'text') {
                    setActiveTextId(Number(clickedElementId.split('-')[1]));
                    setActiveWatermarkId(null);
                    activeElementRef.current = texts.find(t => t.id === Number(clickedElementId.split('-')[1]));
                }

                dragModeRef.current = 'element';
                setIsDragging(true);
                setDragStartX(x);
                setDragStartY(y);
                if (activeElementRef.current) {
                    setInitialElementX(activeElementRef.current.x);
                    setInitialElementY(activeElementRef.current.y);
                }
                currentDragOffsetRef.current = { dx: 0, dy: 0 };
                return;
            }
        }

        // Si no se hizo click en un elemento → iniciar pan
        dragModeRef.current = 'pan';
        isPanningRef.current = true;
        const vp = viewportRef.current;
        panStartRef.current = { x: screenX, y: screenY, vpX: vp.x, vpY: vp.y };
        setActiveWatermarkId(null);
        setActiveTextId(null);
        activeElementRef.current = null;
    };

    const handleInteractionMove = (event) => {
        // Manejar pinch-to-zoom con 2 dedos
        if (event.touches && event.touches.length === 2 && pinchRef.current.active) {
            event.preventDefault();
            const dx = event.touches[0].clientX - event.touches[1].clientX;
            const dy = event.touches[0].clientY - event.touches[1].clientY;
            const currentDist = Math.sqrt(dx * dx + dy * dy);
            const ratio = currentDist / pinchRef.current.startDist;
            const newZoom = Math.max(0.1, Math.min(10, pinchRef.current.startZoom * ratio));

            // Centro del pinch en coordenadas del canvas
            const canvas = canvasRef.current;
            const rect = canvas.getBoundingClientRect();
            const midX = ((event.touches[0].clientX + event.touches[1].clientX) / 2 - rect.left) * (canvas.width / canvas.offsetWidth);
            const midY = ((event.touches[0].clientY + event.touches[1].clientY) / 2 - rect.top) * (canvas.height / canvas.offsetHeight);

            const vp = viewportRef.current;
            vp.x = midX - (midX - vp.x) * (newZoom / vp.zoom);
            vp.y = midY - (midY - vp.y) * (newZoom / vp.zoom);
            vp.zoom = newZoom;

            setViewportTick(v => v + 1);
            drawImagesOnCanvas();

            setShowZoomOverlay(true);
            if (zoomOverlayTimerRef.current) clearTimeout(zoomOverlayTimerRef.current);
            zoomOverlayTimerRef.current = setTimeout(() => setShowZoomOverlay(false), 800);
            return;
        }

        if (!dragModeRef.current) return;
        event.preventDefault();

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        let clientX, clientY;
        if (event.touches && event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }

        const scaleX = canvas.width / canvas.offsetWidth;
        const scaleY = canvas.height / canvas.offsetHeight;
        const screenX = (clientX - rect.left) * scaleX;
        const screenY = (clientY - rect.top) * scaleY;

        if (dragModeRef.current === 'pan') {
            // Mover el viewport
            const start = panStartRef.current;
            viewportRef.current.x = start.vpX + (screenX - start.x);
            viewportRef.current.y = start.vpY + (screenY - start.y);
            drawImagesOnCanvas();
        } else if (dragModeRef.current === 'element' && activeElementRef.current) {
            // Mover el elemento (coordenadas del mundo)
            const { x, y } = getEventCoords(event);
            const dx = x - dragStartX;
            const dy = y - dragStartY;
            currentDragOffsetRef.current = { dx, dy };

            if (!animationFrameIdRef.current) {
                animationFrameIdRef.current = requestAnimationFrame(animateDrag);
            }
        }
    };

    const handleInteractionEnd = (event) => {
        // Limpiar pinch al soltar
        if (event && event.touches && event.touches.length < 2) {
            pinchRef.current.active = false;
        }

        if (dragModeRef.current === 'pan') {
            isPanningRef.current = false;
            dragModeRef.current = null;
            return;
        }

        if (dragModeRef.current === 'element' && isDragging && activeElementRef.current) {
            const finalX = initialElementX + currentDragOffsetRef.current.dx;
            const finalY = initialElementY + currentDragOffsetRef.current.dy;

            if (activeWatermarkId !== null) {
                setWatermarks(prevWatermarks => {
                    return prevWatermarks.map(wm => {
                        if (wm.id === activeWatermarkId) {
                            const scaledWatermarkWidth = wm.obj.width * wm.scale;
                            const scaledWatermarkHeight = wm.obj.height * wm.scale;

                            const boundedX = Math.max(0, Math.min(finalX, canvasRef.current.width / viewportRef.current.zoom - scaledWatermarkWidth));
                            const boundedY = Math.max(0, Math.min(finalY, canvasRef.current.height / viewportRef.current.zoom - scaledWatermarkHeight));

                            return { ...wm, x: boundedX, y: boundedY };
                        }
                        return wm;
                    });
                });
            } else if (activeTextId !== null) {
                setTexts(prevTexts => {
                    return prevTexts.map(txt => {
                        if (txt.id === activeTextId) {
                             const boundedX = Math.max(0, Math.min(finalX, canvasRef.current.width / viewportRef.current.zoom - 100));
                             const boundedY = Math.max(0, Math.min(finalY, canvasRef.current.height / viewportRef.current.zoom - txt.fontSize));

                             return { ...txt, x: boundedX, y: boundedY };
                        }
                        return txt;
                    });
                });
            }
        }

        setIsDragging(false);
        currentDragOffsetRef.current = { dx: 0, dy: 0 };
        activeElementRef.current = null;
        dragModeRef.current = null;
        if (animationFrameIdRef.current) {
            cancelAnimationFrame(animationFrameIdRef.current);
            animationFrameIdRef.current = null;
        }
        drawImagesOnCanvas();
    };

    // --- Fin de funciones para arrastrar ---

    // --- Función de zoom con rueda del mouse ---
    const handleWheel = useCallback((event) => {
        event.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / canvas.offsetWidth;
        const scaleY = canvas.height / canvas.offsetHeight;

        const mouseX = (event.clientX - rect.left) * scaleX;
        const mouseY = (event.clientY - rect.top) * scaleY;

        const vp = viewportRef.current;
        const zoomFactor = event.deltaY < 0 ? 1.08 : 1 / 1.08;
        const newZoom = Math.max(0.1, Math.min(10, vp.zoom * zoomFactor));

        // Zoom centrado en la posición del mouse
        vp.x = mouseX - (mouseX - vp.x) * (newZoom / vp.zoom);
        vp.y = mouseY - (mouseY - vp.y) * (newZoom / vp.zoom);
        vp.zoom = newZoom;

        setViewportTick(v => v + 1);
        drawImagesOnCanvas();

        // Mostrar overlay de zoom fugaz
        setShowZoomOverlay(true);
        if (zoomOverlayTimerRef.current) clearTimeout(zoomOverlayTimerRef.current);
        zoomOverlayTimerRef.current = setTimeout(() => setShowZoomOverlay(false), 800);
    }, [drawImagesOnCanvas]);

    // --- Función de ajustar a pantalla ---
    const fitToScreen = useCallback(() => {
        const canvas = canvasRef.current;
        const img = loadedBaseImageRef.current;
        if (!canvas) return;

        if (img) {
            const padding = 20;
            const availW = canvas.width - padding * 2;
            const availH = canvas.height - padding * 2;
            const zoom = Math.min(availW / img.width, availH / img.height);
            viewportRef.current = {
                x: (canvas.width - img.width * zoom) / 2,
                y: (canvas.height - img.height * zoom) / 2,
                zoom,
            };
        } else {
            viewportRef.current = { x: 0, y: 0, zoom: 1 };
        }
        setViewportTick(v => v + 1);
        drawImagesOnCanvas();
        // Mostrar overlay de zoom fugaz
        setShowZoomOverlay(true);
        if (zoomOverlayTimerRef.current) clearTimeout(zoomOverlayTimerRef.current);
        zoomOverlayTimerRef.current = setTimeout(() => setShowZoomOverlay(false), 800);
    }, [drawImagesOnCanvas]);

    // --- Funciones para arrastrar el panel de ajustes ---
    const handlePanelDragStart = (e) => {
        e.preventDefault();
        setIsPanelDragging(true);
        setInitialPanelPosition({
            x: e.clientX - panelPosition.x,
            y: e.clientY - panelPosition.y
        });
    };

   // Envuelve estas funciones con useCallback
const handlePanelDrag = useCallback((e) => {
    if (!isPanelDragging) return;
    
    const newX = e.clientX - initialPanelPosition.x;
    const newY = e.clientY - initialPanelPosition.y;
    
    // Limitar el movimiento dentro de la ventana
    const maxX = window.innerWidth - (adjustmentPanelRef.current?.offsetWidth || 300);
    const maxY = window.innerHeight - (adjustmentPanelRef.current?.offsetHeight || 400);
    
    setPanelPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
    });
}, [isPanelDragging, initialPanelPosition]);

const handlePanelDragEnd = useCallback(() => {
    setIsPanelDragging(false);
}, []);
    // Agregar event listeners para el arrastre del panel

useEffect(() => {
    if (isPanelDragging) {
        const handleMouseMove = (e) => handlePanelDrag(e);
        const handleMouseUp = () => handlePanelDragEnd();
        const handleTouchMove = (e) => handlePanelDrag(e);
        const handleTouchEnd = () => handlePanelDragEnd();

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }
}, [isPanelDragging, handlePanelDrag, handlePanelDragEnd]);

    // --- Fin de funciones para arrastrar el panel ---

    // Funciones para ajustes
    const handleWatermarkScaleChange = (e) => {
        const newScale = Number(e.target.value);
        if (activeWatermarkId !== null) {
            setWatermarks(prevWatermarks => {
                return prevWatermarks.map(wm => (wm.id === activeWatermarkId) ? { ...wm, scale: newScale } : wm);
            });
        }
    };

    const handleWatermarkOpacityChange = (e) => {
        const newOpacity = Number(e.target.value);
        if (activeWatermarkId !== null) {
            setWatermarks(prevWatermarks => {
                return prevWatermarks.map(wm => (wm.id === activeWatermarkId) ? { ...wm, opacity: newOpacity } : wm);
            });
        }
    };

    const handleElementRotationChange = (e) => {
        const newRotation = Number(e.target.value);
        if (activeWatermarkId !== null) {
            setWatermarks(prev => prev.map(wm => (wm.id === activeWatermarkId) ? { ...wm, rotation: newRotation } : wm));
        } else if (activeTextId !== null) {
            setTexts(prev => prev.map(txt => (txt.id === activeTextId) ? { ...txt, rotation: newRotation } : txt));
        }
    };

    const handleTextContentChange = (e) => {
        const newContent = e.target.value;
        if (activeTextId !== null) {
            setTexts(prevTexts => {
                return prevTexts.map(txt => (txt.id === activeTextId) ? { ...txt, content: newContent } : txt);
            });
        }
    };

    const handleTextFontSizeChange = (e) => {
        const newSize = Number(e.target.value);
        if (activeTextId !== null) {
            setTexts(prevTexts => {
                return prevTexts.map(txt => (txt.id === activeTextId) ? { ...txt, fontSize: newSize } : txt);
            });
        }
    };

    const handleTextFontChange = (e) => {
        const newFontFamily = e.target.value;
        if (activeTextId !== null) {
            setTexts(prevTexts => {
                return prevTexts.map(txt => (txt.id === activeTextId) ? { ...txt, fontFamily: newFontFamily } : txt);
            });
        }
    };

    const handleTextColorChange = (e) => {
        const newColor = e.target.value;
        if (activeTextId !== null) {
            setTexts(prevTexts => {
                return prevTexts.map(txt => (txt.id === activeTextId) ? { ...txt, color: newColor } : txt);
            });
        }
    };

    const handleTextOpacityChange = (e) => {
        const newOpacity = Number(e.target.value);
        if (activeTextId !== null) {
            setTexts(prevTexts => {
                return prevTexts.map(txt => (txt.id === activeTextId) ? { ...txt, opacity: newOpacity } : txt);
            });
        }
    };

    const handleBaseImageRotationChange = (e) => {
        const newRotation = Number(e.target.value);
        setBaseImageRotation(newRotation);
    };

    const handleRemoveElement = () => {
        if (activeWatermarkId !== null) {
            setWatermarks(prevWatermarks => prevWatermarks.filter(wm => wm.id !== activeWatermarkId));
            setActiveWatermarkId(null);
        } else if (activeTextId !== null) {
            setTexts(prevTexts => prevTexts.filter(txt => txt.id !== activeTextId));
            setActiveTextId(null);
        }
        setShowAdjustmentPanel(false);
    };

    const activeWatermark = watermarks.find(wm => wm.id === activeWatermarkId);
    const activeText = texts.find(txt => txt.id === activeTextId);

    const showSuccessToast = (message) => {
        setSuccessMessage(message);
        if (successTimeoutRef.current) {
            clearTimeout(successTimeoutRef.current);
        }
        successTimeoutRef.current = setTimeout(() => {
            setSuccessMessage(null);
            successTimeoutRef.current = null;
        }, 2800);
    };

    const downloadImage = () => {
        const canvas = canvasRef.current;
        if (!canvas || !baseImageSrc) {
            setError(t('errNeedBaseDownload'));
            return;
        }

        const tempCanvas = document.createElement('canvas');
        const originalBaseImage = new Image();
        originalBaseImage.onload = async () => {
            tempCanvas.width = originalBaseImage.width;
            tempCanvas.height = originalBaseImage.height;
            const tempCtx = tempCanvas.getContext('2d');

            // Aplicar rotación a la imagen base en la descarga
            tempCtx.save();
            tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
            tempCtx.rotate(baseImageRotation * Math.PI / 180);
            tempCtx.drawImage(originalBaseImage, -originalBaseImage.width / 2, -originalBaseImage.height / 2);
            tempCtx.restore();

            // Las posiciones de elementos ya están en coordenadas de la imagen original
            for (const watermark of watermarks) {
                if (watermark.obj) {
                    const wmW = watermark.obj.width * watermark.scale;
                    const wmH = watermark.obj.height * watermark.scale;
                    const centerX = watermark.x + wmW / 2;
                    const centerY = watermark.y + wmH / 2;

                    tempCtx.save();
                    tempCtx.translate(centerX, centerY);
                    tempCtx.rotate(watermark.rotation * Math.PI / 180);
                    tempCtx.globalAlpha = watermark.opacity;
                    tempCtx.drawImage(watermark.obj, -wmW / 2, -wmH / 2, wmW, wmH);
                    tempCtx.restore();
                }
            }

            for (const text of texts) {
                tempCtx.font = `${text.fontSize}px '${text.fontFamily}'`;
                const textMetrics = tempCtx.measureText(text.content);
                const textW = textMetrics.width;
                const textH = text.fontSize * 1.2;
                const centerX = text.x + textW / 2;
                const centerY = text.y + textH / 2;

                tempCtx.save();
                tempCtx.translate(centerX, centerY);
                tempCtx.rotate(text.rotation * Math.PI / 180);
                tempCtx.font = `${text.fontSize}px '${text.fontFamily}'`;
                tempCtx.fillStyle = text.color;
                tempCtx.globalAlpha = text.opacity;
                tempCtx.textAlign = 'left';
                tempCtx.textBaseline = 'top';
                tempCtx.fillText(text.content, -textW / 2, -textH / 2);
                tempCtx.restore();
            }

            try {
                const dataUrl = tempCanvas.toDataURL('image/png');

                if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
                    const base64Data = dataUrl.split(',')[1];
                    const fileName = `imagen_editada_${Date.now()}.png`;
                    const downloadsPath = `Download/${fileName}`;

                    try {
                        await Filesystem.requestPermissions();
                    } catch {
                        // Algunos dispositivos no requieren este paso explícito.
                    }

                    await Filesystem.mkdir({
                        path: 'Download',
                        directory: Directory.Documents,
                        recursive: true,
                    }).catch(() => undefined);

                    await Filesystem.writeFile({
                        path: downloadsPath,
                        data: base64Data,
                        directory: Directory.Documents,
                        recursive: true,
                    });

                    showSuccessToast(t('successDownloadAndroid'));
                } else {
                    const link = document.createElement('a');
                    link.download = 'imagen_editada.png';
                    link.href = dataUrl;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    showSuccessToast(t('successDownloadWeb'));
                }

                setError(null);
            } catch (downloadError) {
                setError(`${t('errDownloadDevice')} ${downloadError?.message || t('errUnknown')}`);
            }
        };
        originalBaseImage.onerror = () => {
            setError(t('errBaseDownload'));
        };
        originalBaseImage.src = baseImageSrc;
    };

    const handleReset = () => {
        setBaseImageSrc(null);
        setBaseImageRotation(0);
        loadedBaseImageRef.current = null;
        setWatermarks([]);
        setTexts([]);
        setNextWatermarkId(0);
        setNextTextId(0);
        setActiveWatermarkId(null);
        setActiveTextId(null);
        setLoading(false);
        setError(null);
        setSuccessMessage(null);
        setShowAdjustmentPanel(false);
        setShowHelpModal(false);
        setShowAddTextModal(false);
        setPanelPosition({ x: 0, y: 0 }); // Resetear posición del panel
        viewportRef.current = { x: 0, y: 0, zoom: 1 }; // Resetear viewport
        setViewportTick(v => v + 1);
        if (baseImageInputRef.current) baseImageInputRef.current.value = '';
        if (watermarkInputRef.current) watermarkInputRef.current.value = '';
    };

    useEffect(() => {
        return () => {
            if (successTimeoutRef.current) {
                clearTimeout(successTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        window.localStorage.setItem('app-language', language);
    }, [language]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            // Si hay una imagen base, marcas de agua o textos, consideramos que hay cambios sin guardar.
            if (baseImageSrc) {
                e.preventDefault(); // Requerido por el estándar.
                e.returnValue = ''; // Requerido para navegadores más antiguos.
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [baseImageSrc]); // Se ejecuta solo cuando cambia la imagen base.

    useEffect(() => {
        const isModalOpen = showAdjustmentPanel || showHelpModal || showAddTextModal;

        const handlePopstate = (e) => {
            if (isModalOpen) {
                e.preventDefault();
                setShowAdjustmentPanel(false);
                setShowHelpModal(false);
                setShowAddTextModal(false);
            }
        };

        if (isModalOpen) {
            window.history.pushState(null, '', '#modal-open');
            window.addEventListener('popstate', handlePopstate);
        }

        return () => {
            if (isModalOpen) {
                window.removeEventListener('popstate', handlePopstate);
                if (window.location.hash === '#modal-open') {
                    window.history.back();
                }
            }
        };
    }, [showAdjustmentPanel, showHelpModal, showAddTextModal]);

    return (
        <div className="app-root p-1 sm:p-3 flex flex-col items-center font-sans relative">
            <style>
                {`
                .app-root {
                    height: 100vh;
                    height: 100dvh;
                }
                body {
                    font-family: 'Inter', sans-serif;
                    overscroll-behavior-y: contain;
                    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 30%, #f0f0f5 60%, #ede9fe 100%);
                    min-height: 100vh;
                }
                input[type="range"]::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #6366f1, #8b5cf6);
                    cursor: pointer;
                    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.25), 0 2px 8px rgba(99, 102, 241, 0.4);
                    transition: all .2s ease-in-out;
                }
                input[type="range"]::-webkit-slider-thumb:hover {
                    box-shadow: 0 0 0 5px rgba(99, 102, 241, 0.35), 0 2px 12px rgba(99, 102, 241, 0.5);
                    transform: scale(1.1);
                }
                input[type="range"]::-moz-range-thumb {
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #6366f1, #8b5cf6);
                    cursor: pointer;
                    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.25), 0 2px 8px rgba(99, 102, 241, 0.4);
                    border: none;
                }
                input[type="range"]::-webkit-slider-runnable-track {
                    width: 100%;
                    height: 6px;
                    background: linear-gradient(90deg, #e0e7ff, #c7d2fe);
                    border-radius: 999px;
                }
                input[type="range"]::-moz-range-track {
                    width: 100%;
                    height: 6px;
                    background: linear-gradient(90deg, #e0e7ff, #c7d2fe);
                    border-radius: 999px;
                }
                .icon-button {
                    position: relative;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 42px;
                    height: 42px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #6366f1, #818cf8);
                    color: white;
                    border: none;
                    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35), 0 1px 3px rgba(0, 0, 0, 0.12);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    cursor: pointer;
                    outline: none;
                }
                .icon-button::before {
                    content: '';
                    position: absolute;
                    inset: -3px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, rgba(99, 102, 241, 0), rgba(139, 92, 246, 0));
                    transition: all 0.3s ease;
                    z-index: -1;
                }
                .icon-button:hover {
                    transform: translateY(-2px) scale(1.08);
                    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.5), 0 2px 6px rgba(0, 0, 0, 0.15);
                }
                .icon-button:hover::before {
                    background: linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(139, 92, 246, 0.3));
                    inset: -5px;
                }
                .icon-button:active {
                    transform: translateY(0px) scale(0.96);
                    box-shadow: 0 1px 4px rgba(99, 102, 241, 0.3);
                }
                .icon-button:disabled {
                    opacity: 0.45;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }
                .icon-button:disabled:hover {
                    transform: none;
                    box-shadow: none;
                }
                .icon-button.btn-yellow {
                    background: linear-gradient(135deg, #f59e0b, #fbbf24);
                    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.35), 0 1px 3px rgba(0, 0, 0, 0.12);
                }
                .icon-button.btn-yellow:hover {
                    box-shadow: 0 4px 16px rgba(245, 158, 11, 0.5), 0 2px 6px rgba(0, 0, 0, 0.15);
                }
                .icon-button.btn-yellow:hover::before {
                    background: linear-gradient(135deg, rgba(245, 158, 11, 0.3), rgba(251, 191, 36, 0.3));
                }
                .icon-button.btn-blue {
                    background: linear-gradient(135deg, #3b82f6, #60a5fa);
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.35), 0 1px 3px rgba(0, 0, 0, 0.12);
                }
                .icon-button.btn-blue:hover {
                    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.5), 0 2px 6px rgba(0, 0, 0, 0.15);
                }
                .icon-button.btn-blue:hover::before {
                    background: linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(96, 165, 250, 0.3));
                }
                .icon-button.btn-gray {
                    background: linear-gradient(135deg, #6b7280, #9ca3af);
                    box-shadow: 0 2px 8px rgba(107, 114, 128, 0.3), 0 1px 3px rgba(0, 0, 0, 0.12);
                }
                .icon-button.btn-gray:hover {
                    box-shadow: 0 4px 16px rgba(107, 114, 128, 0.45), 0 2px 6px rgba(0, 0, 0, 0.15);
                }
                .icon-button.btn-gray:hover::before {
                    background: linear-gradient(135deg, rgba(107, 114, 128, 0.3), rgba(156, 163, 175, 0.3));
                }
                .icon-button.btn-green {
                    background: linear-gradient(135deg, #22c55e, #4ade80);
                    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.35), 0 1px 3px rgba(0, 0, 0, 0.12);
                }
                .icon-button.btn-green:hover {
                    box-shadow: 0 4px 16px rgba(34, 197, 94, 0.5), 0 2px 6px rgba(0, 0, 0, 0.15);
                }
                .icon-button.btn-green:hover::before {
                    background: linear-gradient(135deg, rgba(34, 197, 94, 0.3), rgba(74, 222, 128, 0.3));
                }
                .icon-button.btn-slate {
                    background: linear-gradient(135deg, #475569, #64748b);
                    box-shadow: 0 2px 8px rgba(71, 85, 105, 0.35), 0 1px 3px rgba(0, 0, 0, 0.12);
                }
                .icon-button.btn-slate:hover {
                    box-shadow: 0 4px 16px rgba(71, 85, 105, 0.5), 0 2px 6px rgba(0, 0, 0, 0.15);
                }
                .icon-button.btn-slate:hover::before {
                    background: linear-gradient(135deg, rgba(71, 85, 105, 0.3), rgba(100, 116, 139, 0.3));
                }
                .icon-button.btn-red {
                    background: linear-gradient(135deg, #ef4444, #f87171);
                    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.35), 0 1px 3px rgba(0, 0, 0, 0.12);
                }
                .icon-button.btn-red:hover {
                    box-shadow: 0 4px 16px rgba(239, 68, 68, 0.5), 0 2px 6px rgba(0, 0, 0, 0.15);
                }
                .icon-button.btn-red:hover::before {
                    background: linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(248, 113, 113, 0.3));
                }
                .toolbar-glass {
                    background: rgba(255, 255, 255, 0.85);
                    backdrop-filter: blur(12px);
                    -webkit-backdrop-filter: blur(12px);
                    border: 1px solid rgba(255, 255, 255, 0.6);
                    border-bottom: 2px solid transparent;
                    border-image: linear-gradient(90deg, #6366f1, #8b5cf6, #a78bfa, #8b5cf6, #6366f1) 1;
                    border-image-slice: 0 0 1 0;
                }
                .modal-glass {
                    background: rgba(255, 255, 255, 0.92);
                    backdrop-filter: blur(16px);
                    -webkit-backdrop-filter: blur(16px);
                    border: 1px solid rgba(255, 255, 255, 0.7);
                }
                .canvas-wrapper {
                    background: white;
                    box-shadow: 0 4px 24px rgba(99, 102, 241, 0.12), 0 1px 4px rgba(0, 0, 0, 0.06), inset 0 0 0 1px rgba(99, 102, 241, 0.08);
                    border: 1px solid rgba(99, 102, 241, 0.15);
                }
                .draggable-panel {
                    cursor: move;
                    user-select: none;
                }
                .canvas-interactive {
                    touch-action: none;
                    cursor: grab;
                }
                .canvas-interactive:active {
                    cursor: grabbing;
                }
                @media (max-width: 480px) {
                    .icon-button {
                        width: 38px;
                        height: 38px;
                    }
                }
                .toast-notification {
                    animation: toast-slide-in 0.35s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08);
                }
                .zoom-overlay {
                    animation: zoom-fade 0.8s ease forwards;
                }
                @keyframes zoom-fade {
                    0% { opacity: 0; transform: scale(0.8); }
                    15% { opacity: 1; transform: scale(1); }
                    70% { opacity: 1; transform: scale(1); }
                    100% { opacity: 0; transform: scale(0.95); }
                }
                @keyframes toast-slide-in {
                    from {
                        opacity: 0;
                        transform: translateY(16px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .btn-full-gradient {
                    background: linear-gradient(135deg, #6366f1, #8b5cf6);
                    color: white;
                    font-weight: 600;
                    padding: 0.5rem 1.25rem;
                    border-radius: 9999px;
                    border: none;
                    box-shadow: 0 2px 10px rgba(99, 102, 241, 0.35);
                    transition: all 0.25s ease;
                    cursor: pointer;
                }
                .btn-full-gradient:hover {
                    box-shadow: 0 4px 18px rgba(99, 102, 241, 0.5);
                    transform: translateY(-1px);
                }
                .btn-full-gradient:active {
                    transform: translateY(0);
                }
                .btn-cancel {
                    background: linear-gradient(135deg, #94a3b8, #cbd5e1);
                    color: #334155;
                    font-weight: 600;
                    padding: 0.5rem 1.25rem;
                    border-radius: 9999px;
                    border: none;
                    box-shadow: 0 2px 8px rgba(148, 163, 184, 0.3);
                    transition: all 0.25s ease;
                    cursor: pointer;
                }
                .btn-cancel:hover {
                    box-shadow: 0 4px 14px rgba(148, 163, 184, 0.45);
                    transform: translateY(-1px);
                }
                .btn-danger-full {
                    background: linear-gradient(135deg, #ef4444, #f87171);
                    color: white;
                    font-weight: 600;
                    padding: 0.5rem 1.25rem;
                    border-radius: 9999px;
                    border: none;
                    box-shadow: 0 2px 10px rgba(239, 68, 68, 0.35);
                    transition: all 0.25s ease;
                    cursor: pointer;
                }
                .btn-danger-full:hover {
                    box-shadow: 0 4px 18px rgba(239, 68, 68, 0.5);
                    transform: translateY(-1px);
                }
                .header-gradient {
                    background: linear-gradient(180deg, rgba(99, 102, 241, 0.06) 0%, transparent 100%);
                }
                .title-gradient {
                    background: linear-gradient(135deg, #4338ca 0%, #6366f1 50%, #7c3aed 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                `}
            </style>

            <header className="header-gradient w-full max-w-4xl rounded-2xl px-4 pt-2 pb-1 mb-1 text-center">
                <h1 className="text-lg sm:text-xl font-bold title-gradient leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    AquaSigna
                </h1>
                <p className="text-[11px] sm:text-xs text-indigo-500 font-medium">
                    {t('appSubtitle')}
                </p>
            </header>

            <div
                ref={canvasContainerRef}
                className="canvas-wrapper p-1 sm:p-3 rounded-2xl w-full max-w-4xl overflow-hidden relative flex-1 min-h-0"
            >
                <canvas
                    ref={canvasRef}
                    className="rounded-lg canvas-interactive block"
                    style={{ width: '100%', height: '100%' }}
                    onMouseDown={handleInteractionStart}
                    onMouseMove={handleInteractionMove}
                    onMouseUp={handleInteractionEnd}
                    onMouseLeave={handleInteractionEnd}
                    onTouchStart={handleInteractionStart}
                    onTouchMove={handleInteractionMove}
                    onTouchEnd={handleInteractionEnd}
                    onTouchCancel={handleInteractionEnd}
                    onWheel={handleWheel}
                ></canvas>
                {showZoomOverlay && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 zoom-overlay">
                        <span className="text-4xl sm:text-5xl font-bold text-white/90 bg-black/40 backdrop-blur-sm rounded-2xl px-5 py-2 font-mono select-none" style={{ fontFamily: "'Poppins', sans-serif" }}>
                            {Math.round(viewportRef.current.zoom * 100)}%
                        </span>
                    </div>
                )}
            </div>

            <div className="w-full max-w-4xl flex flex-nowrap justify-center items-center gap-1.5 sm:gap-3 mt-1.5 mb-1.5 py-1.5 px-1.5 sm:p-2.5 toolbar-glass rounded-2xl overflow-x-auto">
                <button
                    onClick={() => baseImageInputRef.current.click()}
                    className="icon-button shrink-0"
                    title={t('selectBaseImageTitle')}
                >
                    <FileUp size={20} />
                </button>
                <input
                    type="file"
                    id="baseImage"
                    accept="image/*"
                    onChange={handleBaseImageUpload}
                    className="hidden"
                    ref={baseImageInputRef}
                />

                <div className="w-px h-8 bg-indigo-200/60 shrink-0 hidden sm:block"></div>

                <button
                    onClick={() => watermarkInputRef.current.click()}
                    className={`icon-button shrink-0 ${!baseImageSrc ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={t('addWatermarkTitle')}
                    disabled={!baseImageSrc}
                >
                    <ImagePlus size={20} />
                </button>
                <input
                    type="file"
                    id="addWatermark"
                    accept="image/*"
                    onChange={handleAddWatermark}
                    className="hidden"
                    ref={watermarkInputRef}
                    key={nextWatermarkId}
                />

                <button
                    onClick={handleAddText}
                    className={`icon-button btn-yellow shrink-0 ${!baseImageSrc ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={t('addTextTitle')}
                    disabled={!baseImageSrc}
                >
                    <Type size={20} />
                </button>

                <div className="w-px h-8 bg-indigo-200/60 shrink-0 hidden sm:block"></div>

                <button
                    onClick={() => setShowAdjustmentPanel(prev => !prev)}
                    className={`icon-button btn-blue shrink-0 ${!baseImageSrc ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={t('adjustTitle')}
                    disabled={!baseImageSrc}
                >
                    <SlidersHorizontal size={20} />
                </button>

                <button
                    onClick={handleReset}
                    className={`icon-button btn-gray shrink-0 ${!baseImageSrc ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={t('resetTitle')}
                    disabled={!baseImageSrc}
                >
                    <RotateCcw size={20} />
                </button>

                <button
                    onClick={downloadImage}
                    className={`icon-button btn-green shrink-0 ${!baseImageSrc ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={t('downloadTitle')}
                    disabled={!baseImageSrc}
                >
                    <Download size={20} />
                </button>

                <div className="w-px h-8 bg-indigo-200/60 shrink-0 hidden sm:block"></div>

                <button
                    onClick={fitToScreen}
                    className={`icon-button btn-blue shrink-0 relative ${!baseImageSrc ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={t('fitScreenTitle')}
                    disabled={!baseImageSrc}
                >
                    <Maximize size={20} />
                </button>

                <button
                    onClick={() => setShowHelpModal(prev => !prev)}
                    className="icon-button btn-slate shrink-0"
                    title={t('settingsTitleButton')}
                >
                    <Settings size={20} />
                </button>
            </div>

            {(showAdjustmentPanel && (activeWatermark || activeText || baseImageSrc)) && (
                <div className="fixed inset-0 bg-gradient-to-br from-black/40 via-black/50 to-indigo-900/30 flex justify-center items-center z-50 backdrop-blur-sm"
                     onClick={() => setShowAdjustmentPanel(false)}>
                    <div
                        ref={adjustmentPanelRef}
                        className="modal-glass p-6 rounded-2xl shadow-2xl flex flex-col gap-4 w-11/12 max-w-sm absolute max-h-[85vh] overflow-y-auto"
                         style={{ 
                             backgroundColor: `rgba(255, 255, 255, ${modalOpacity})`,
                             ...(panelPosition.x !== 0 || panelPosition.y !== 0 ? {
                                 left: `${panelPosition.x}px`,
                                 top: `${panelPosition.y}px`
                             } : {}),
                             cursor: isPanelDragging ? 'grabbing' : 'default'
                         }}
                         onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4 border-b-2 border-indigo-100 pb-3">
                            <div
                                className="draggable-panel flex items-center gap-2 cursor-move flex-grow"
                                onMouseDown={handlePanelDragStart}
                                onTouchStart={handlePanelDragStart}
                            >
                                <h3 className="text-xl font-bold title-gradient" style={{ fontFamily: "'Poppins', sans-serif" }}>Ajustar Elemento</h3>
                                <Move size={16} className="text-indigo-400" />
                            </div>
                            <button
                                onClick={() => setShowAdjustmentPanel(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-indigo-50 transition-all duration-200"
                                title="Cerrar"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        {activeWatermark && (
                            <>
                                <div className="mb-4">
                                    <label htmlFor="watermarkScale" className="block text-gray-700 text-sm font-medium mb-2">
                                        Escala: {(activeWatermark.scale * 100).toFixed(0)}%
                                    </label>
                                    <input
                                        type="range"
                                        id="watermarkScale"
                                        min="0.01"
                                        max="3.0"
                                        step="0.01"
                                        value={activeWatermark.scale}
                                        onChange={handleWatermarkScaleChange}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label htmlFor="watermarkOpacity" className="block text-gray-700 text-sm font-medium mb-2">
                                        Opacidad de la Marca de Agua: {(activeWatermark.opacity * 100).toFixed(0)}%
                                    </label>
                                    <input
                                        type="range"
                                        id="watermarkOpacity"
                                        min="0.0"
                                        max="1.0"
                                        step="0.01"
                                        value={activeWatermark.opacity}
                                        onChange={handleWatermarkOpacityChange}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                    />
                                </div>
                            </>
                        )}
                        {baseImageSrc && !activeWatermark && !activeText && (
                            <div className="mb-4">
                                <label htmlFor="baseImageRotation" className="block text-gray-700 text-sm font-medium mb-2">
                                    Rotación Imagen Base: {baseImageRotation.toFixed(0)}°
                                </label>
                                <input
                                    type="range"
                                    id="baseImageRotation"
                                    min="-180"
                                    max="180"
                                    step="1"
                                    value={baseImageRotation}
                                    onChange={handleBaseImageRotationChange}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                />
                            </div>
                        )}

                        {(activeWatermark || activeText) && (
                            <div className="mb-4">
                                <label htmlFor="elementRotation" className="block text-gray-700 text-sm font-medium mb-2">
                                    Rotación: {(activeWatermark?.rotation || activeText?.rotation || 0).toFixed(0)}°
                                </label>
                                <input
                                    type="range"
                                    id="elementRotation"
                                    min="-180"
                                    max="180"
                                    step="1"
                                    value={activeWatermark?.rotation || activeText?.rotation || 0}
                                    onChange={handleElementRotationChange}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                />
                            </div>
                        )}
                        {activeText && (
                            <>
                                <div className="mb-4">
                                    <label htmlFor="textContent" className="block text-gray-700 text-sm font-medium mb-2">
                                        Contenido del Texto
                                    </label>
                                    <input
                                        type="text"
                                        id="textContent"
                                        value={activeText.content}
                                        onChange={handleTextContentChange}
                                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label htmlFor="textFont" className="block text-gray-700 text-sm font-medium mb-2">
                                        Fuente
                                    </label>
                                    <select
                                        id="textFont"
                                        value={activeText.fontFamily}
                                        onChange={handleTextFontChange}
                                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                                    >
                                        {FONT_OPTIONS.map(font => (
                                            <option key={font} value={font}>
                                                {font}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-4">
                                    <label htmlFor="textFontSize" className="block text-gray-700 text-sm font-medium mb-2">
                                        Tamaño de Fuente: {activeText.fontSize}px
                                    </label>
                                    <input
                                        type="range"
                                        id="textFontSize"
                                        min={Math.max(8, Math.round(activeText.fontSize * 0.1))}
                                        max={Math.min(300, Math.round(activeText.fontSize * 5))}
                                        step="1"
                                        value={activeText.fontSize}
                                        onChange={handleTextFontSizeChange}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label htmlFor="textColor" className="block text-gray-700 text-sm font-medium mb-2">
                                        Color de Texto
                                    </label>
                                    <input
                                        type="color"
                                        id="textColor"
                                        value={activeText.color}
                                        onChange={handleTextColorChange}
                                        className="w-full h-10 cursor-pointer"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label htmlFor="textOpacity" className="block text-gray-700 text-sm font-medium mb-2">
                                        Opacidad del Texto: {(activeText.opacity * 100).toFixed(0)}%
                                    </label>
                                    <input
                                        type="range"
                                        id="textOpacity"
                                        min="0.0"
                                        max="1.0"
                                        step="0.01"
                                        value={activeText.opacity}
                                        onChange={handleTextOpacityChange}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                    />
                                </div>
                            </>
                        )}
                        {/* Control de opacidad para el panel modal */}
                        <div className="mb-4">
                            <label htmlFor="modalOpacity" className="block text-gray-700 text-sm font-medium mb-2">
                                Opacidad del Panel: {(modalOpacity * 100).toFixed(0)}%
                            </label>
                            <input
                                type="range"
                                id="modalOpacity"
                                min="0.1"
                                max="1.0"
                                step="0.01"
                                value={modalOpacity}
                                onChange={(e) => setModalOpacity(Number(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>
                        <button
                            onClick={handleRemoveElement}
                            className="btn-danger-full w-full flex items-center justify-center gap-2"
                        >
                            <Trash2 size={18} /> {t('removeElement')}
                        </button>
                        <p className="text-sm text-gray-600 mt-2 text-center">
                            {t('dragHint')}
                        </p>
                    </div>
                </div>
            )}

            {showAddTextModal && (
                <div className="fixed inset-0 bg-gradient-to-br from-black/40 via-black/50 to-indigo-900/30 flex justify-center items-center z-50 backdrop-blur-sm"
                     onClick={() => setShowAddTextModal(false)}>
                    <div className="modal-glass p-6 rounded-2xl shadow-2xl flex flex-col gap-4 w-11/12 max-w-sm max-h-[85vh] overflow-y-auto"
                         onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-2 border-b-2 border-indigo-100 pb-3">
                            <h3 className="text-xl font-bold title-gradient" style={{ fontFamily: "'Poppins', sans-serif" }}>{t('addTextModalTitle')}</h3>
                            <button
                                onClick={() => setShowAddTextModal(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-indigo-50 transition-all duration-200"
                                title={t('close')}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <input
                            type="text"
                            className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                            value={newTextContent}
                            onChange={(e) => setNewTextContent(e.target.value)}
                            placeholder={t('addTextPlaceholder')}
                        />
                        <button
                            onClick={handleCreateText}
                            className="btn-full-gradient"
                        >
                            {t('createText')}
                        </button>
                        <button
                            onClick={() => setShowAddTextModal(false)}
                            className="btn-cancel"
                        >
                            {t('cancel')}
                        </button>
                    </div>
                </div>
            )}

            {showHelpModal && (
                <div className="fixed inset-0 bg-gradient-to-br from-black/40 via-black/50 to-indigo-900/30 flex justify-center items-center z-50 backdrop-blur-sm"
                     onClick={() => setShowHelpModal(false)}>
                    <div className="modal-glass p-6 rounded-2xl shadow-2xl flex flex-col gap-4 w-11/12 max-w-lg max-h-[85vh] overflow-y-auto"
                         onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-2 border-b-2 border-indigo-100 pb-3">
                            <h3 className="text-xl font-bold title-gradient" style={{ fontFamily: "'Poppins', sans-serif" }}>{t('settingsModalTitle')}</h3>
                            <button
                                onClick={() => setShowHelpModal(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-indigo-50 transition-all duration-200"
                                title={t('close')}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <section className="border border-indigo-100 rounded-xl p-4 bg-gradient-to-br from-indigo-50/50 to-white">
                            <h4 className="font-semibold text-gray-800 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>{t('languageSection')}</h4>
                            <div className="flex gap-2">
                                <button
                                    className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all duration-200 ${language === 'es' ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white border-transparent shadow-md shadow-indigo-200' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600'}`}
                                    onClick={() => setLanguage('es')}
                                >
                                    {t('languageSpanish')}
                                </button>
                                <button
                                    className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all duration-200 ${language === 'en' ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white border-transparent shadow-md shadow-indigo-200' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600'}`}
                                    onClick={() => setLanguage('en')}
                                >
                                    {t('languageEnglish')}
                                </button>
                            </div>
                        </section>
                        <section className="border border-indigo-100 rounded-xl p-4 bg-gradient-to-br from-indigo-50/50 to-white text-sm text-gray-600">
                            <h4 className="font-semibold text-gray-800 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>{t('companySection')}</h4>
                            <p>{t('author')}</p>
                            <p>{t('company')}</p>
                            <p>{t('versionLabel')} {APP_VERSION}</p>
                            <p>
                                {t('contactEmail')} 
                                <a
                                    href="mailto:info@solinme.com"
                                    className="text-indigo-700 hover:text-indigo-900 underline"
                                >
                                    info@solinme.com
                                </a>
                            </p>
                            <p>
                                {t('contactWeb')} 
                                <a
                                    href="https://www.solinme.com"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-indigo-700 hover:text-indigo-900 underline"
                                >
                                    www.solinme.com
                                </a>
                            </p>
                        </section>
                        <section className="border border-indigo-100 rounded-xl p-4 bg-gradient-to-br from-indigo-50/50 to-white">
                            <h4 className="font-semibold text-gray-800 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>{t('helpSection')}</h4>
                        <ul className="space-y-3 text-gray-700">
                            <li className="flex items-center gap-3">
                                <FileUp size={24} className="text-indigo-600 flex-shrink-0" />
                                <div>
                                    {t('helpBaseImage')}
                                </div>
                            </li>
                            <li className="flex items-center gap-3">
                                <ImagePlus size={24} className="text-indigo-600 flex-shrink-0" />
                                <div>
                                    {t('helpWatermark')}
                                </div>
                            </li>
                             <li className="flex items-center gap-3">
                                <Type size={24} className="text-yellow-600 flex-shrink-0" />
                                <div>
                                    {t('helpText')}
                                </div>
                            </li>
                            <li className="flex items-center gap-3">
                                <SlidersHorizontal size={24} className="text-blue-600 flex-shrink-0" />
                                <div>
                                    {t('helpAdjust')}
                                </div>
                            </li>
                            <li className="flex items-center gap-3">
                                <RotateCcw size={24} className="text-gray-600 flex-shrink-0" />
                                <div>
                                    {t('helpReset')}
                                </div>
                            </li>
                            <li className="flex items-center gap-3">
                                <Download size={24} className="text-green-600 flex-shrink-0" />
                                <div>
                                    {t('helpDownload')}
                                </div>
                            </li>
                            <li className="flex items-center gap-3">
                                <Maximize size={24} className="text-blue-500 flex-shrink-0" />
                                <div>
                                    {t('helpFitScreen')}
                                </div>
                            </li>
                        </ul>
                        </section>
                    </div>
                </div>
            )}

            {loading && (
                <p className="fixed bottom-4 left-4 z-50 text-indigo-600 bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-lg border border-indigo-100 flex items-center gap-2 toast-notification">
                    <span className="inline-block w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin"></span>
                    {t('loading')}
                </p>
            )}
            {error && (
                <p className="fixed bottom-4 left-4 z-50 text-red-600 bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-lg border border-red-100 flex items-center gap-2 toast-notification">
                    <span className="text-red-500 text-lg">!</span>
                    {error}
                </p>
            )}
            {successMessage && (
                <p className="fixed bottom-4 left-4 z-50 text-green-600 bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-lg border border-green-100 flex items-center gap-2 toast-notification">
                    <span className="text-green-500 text-lg">&#10003;</span>
                    {successMessage}
                </p>
            )}
        </div>
    );
};

export default App;